// TODO: Set up TS for this file.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import ErrorIcon from '@curology/ui-components-web/react/Icon/icons/ErrorIcon';
import { type DataCollector, type PayPalCheckout } from 'braintree-web';
import React from 'react';

import { type FlashMessageProps } from '@/atoms/flashMessages/flashMessagesState';
import { type Brand } from '@/consts/brands';
import { APP_ENV } from '@/consts/environmental';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { getPaymentErrorMessage } from '@/helpers/productSelectionAndCheckout/getPaymentErrorMessage';
import { renderFlashMessageOnCheckoutSuccess } from '@/helpers/productSelectionAndCheckout/helpers';
import { reportUnhandledPaymentErrorToSentry } from '@/helpers/productSelectionAndCheckout/reportUnhandledPaymentErrorToSentry';
import { updateRegistrationEligibility } from '@/services/eligibilityService/actions';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { type IneligibleReasonType } from '@/types/eligibility/eligibility';
import {
  type Patient,
  type PayPalActivateExpressCheckoutTrialData,
  type PayPalAuthorizationResponse,
  type ValidationErrorResponse,
} from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import { type HttpError } from '@/utils/http/client';

// Logic comes from:
// resources/assets/jsx/signup/views/recommendations/components/newCurologyControlExperience/js/usePaypalExpressCheckoutHandler.tsx

export const payPal: {
  buttonContainerSelector: string;
  renderCount: number;
  selectedItemIds: number[];
  isRendering: boolean;
} = {
  buttonContainerSelector: '#paypal-button-container',
  renderCount: 0,
  selectedItemIds: [],
  isRendering: false,
};

const clearPayPalButtonContainer = () => {
  const payPalButtonContainer = document.querySelector(
    payPal.buttonContainerSelector,
  );

  if (payPalButtonContainer) {
    payPalButtonContainer.innerHTML = '';
  }
};

const getActivateExpressCheckoutTrialData = (
  patient: Patient,
  dataCollectorInstance: DataCollector,
  authorizationResponse: PayPalAuthorizationResponse,
) => {
  let firstname = '';
  let lastname = '';

  if (patient.firstname && patient.lastname) {
    firstname = patient.firstname;
    lastname = patient.lastname;
  } else {
    firstname = authorizationResponse.details.firstName.trim();
    lastname = authorizationResponse.details.lastName.trim();
  }

  if (firstname === '' || lastname === '') {
    return;
  }

  const { deviceData } = dataCollectorInstance;

  const {
    details: { shippingAddress },
    nonce: paymentNonce,
  } = authorizationResponse;
  const {
    city = '',
    line1: address1 = '',
    line2: address2,
    postalCode: zipcode = '',
    state = '',
  } = shippingAddress ?? {};

  const method = 'paypal';
  const phoneNumberOptional = true;
  const vendor = 'braintree';

  return {
    address1,
    address2,
    city,
    deviceData,
    firstname,
    lastname,
    method,
    paymentNonce,
    phoneNumberOptional,
    state,
    vendor,
    zipcode,
  };
};

export const initPayPal = async ({
  dataCollector,
  payPalCheckout,
  brand,
  flashMessages,
  patient,
  setFlashMessages,
  setShowLoader,
  showFlashMessageOnCheckoutSuccess,
  renderCount,
  onSubmit,
  treatmentCategory,
  onSuccess,
  onIneligible,
}: {
  dataCollector: DataCollector;
  payPalCheckout: PayPalCheckout;
  brand: Brand;
  flashMessages: FlashMessageProps[];
  patient: Patient;
  setFlashMessages: React.Dispatch<React.SetStateAction<FlashMessageProps[]>>;
  setShowLoader: React.Dispatch<React.SetStateAction<boolean>>;
  showFlashMessageOnCheckoutSuccess?: boolean;
  renderCount: number;
  onSubmit: (args: {
    brand: Brand;
    data: PayPalActivateExpressCheckoutTrialData;
    treatmentCategory: TreatmentCategory;
  }) => Promise<void>;
  treatmentCategory: TreatmentCategory;
  onSuccess?: () => void;
  onIneligible?: (ineligibleReason: IneligibleReasonType) => void;
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  // Prevent concurrent renders that cause "Page already has an active payment session"
  if (payPal.isRendering) {
    return;
  }

  clearPayPalButtonContainer();

  if (
    renderCount === payPal.renderCount &&
    document.querySelector(payPal.buttonContainerSelector)
  ) {
    payPal.isRendering = true;
    trackEvent(AnalyticsEvent.PAYPAL_EXPRESS_CHECKOUT_LOADED);

    setShowLoader(false);

    window['paypal'].Button.render(
      {
        env: APP_ENV === 'production' ? 'production' : 'sandbox',
        onAuthorize: async (data) => {
          const authorizationResponse =
            await payPalCheckout.tokenizePayment(data);
          try {
            const activateExpressCheckoutTrialData =
              getActivateExpressCheckoutTrialData(
                patient,
                dataCollector,
                authorizationResponse,
              );

            if (activateExpressCheckoutTrialData) {
              const registrationEligibilityResponse =
                await updateRegistrationEligibility(brand, {
                  state: activateExpressCheckoutTrialData.state,
                  treatmentCategories: treatmentCategory,
                });

              if (registrationEligibilityResponse?.ineligibleReason) {
                if (onIneligible) {
                  onIneligible(
                    registrationEligibilityResponse.ineligibleReason,
                  );
                }

                return false;
              }

              await onSubmit({
                brand,
                data: activateExpressCheckoutTrialData,
                treatmentCategory,
              });

              if (onSuccess) {
                if (showFlashMessageOnCheckoutSuccess) {
                  renderFlashMessageOnCheckoutSuccess(
                    flashMessages,
                    setFlashMessages,
                  );
                }

                onSuccess();
              }
            } else {
              setFlashMessages([
                ...flashMessages,
                {
                  children:
                    'Please edit the name associated with PayPal so that there is both a first name and last name, and then try again. If you think this message is in error, please contact our Customer Success team at hello@curology.com.',
                  icon: <ErrorIcon />,
                  iconSize: 'sm',
                  state: 'critical',
                  title: 'Oops!',
                },
              ]);
            }
          } catch (exception) {
            reportUnhandledPaymentErrorToSentry(exception);
            const err = exception as HttpError<ValidationErrorResponse>;
            const message = getPaymentErrorMessage(
              err?.response?.data,
              'There was an error completing PayPal checkout. Please try again.',
            );
            setFlashMessages([
              ...flashMessages,
              {
                children: message,
                icon: <ErrorIcon />,
                iconSize: 'sm',
                state: 'critical',
                title: 'Oops!',
              },
            ]);
            return authorizationResponse;
          }
          trackEvent(AnalyticsEvent.PAYPAL_ACCOUNT_LINKED);
          return authorizationResponse;
        },
        onClick: async () => {
          if (payPal.selectedItemIds.length > 0) {
            await ProductSelectionAndCheckoutService.updateSelectedItems(
              brand,
              payPal.selectedItemIds,
              treatmentCategory,
            );

            trackEvent(AnalyticsEvent.EXPRESS_CHECKOUT_CLICKED, {
              paymentMethod: 'Paypal',
            });
          } else {
            setFlashMessages([
              ...flashMessages,
              {
                children: 'Please select at least one product to proceed.',
                icon: <ErrorIcon />,
                iconSize: 'sm',
                state: 'critical',
                title: 'Whoops!',
              },
            ]);
          }
        },
        payment: () =>
          payPalCheckout.createPayment({
            displayName: brand,
            enableShippingAddress: true,
            flow: 'vault',
            intent: 'authorize',
          }),
        style: {
          height: 55,
          label: 'paypal',
          shape: 'pill',
          size: 'responsive',
          tagline: false,
        },
      },
      payPal.buttonContainerSelector,
    );
  }
};

/**
 * Resets the PayPal rendering state. Should be called when the PayPal button
 * component unmounts to allow a fresh render on re-mount.
 */
export const resetPayPalRenderState = () => {
  payPal.isRendering = false;
  payPal.renderCount = 0;
  clearPayPalButtonContainer();
};
